import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { NutritionService } from '../services/nutritionService';
import type { NutritionistProfileData } from '../services/nutritionService';

const GOAL_TYPE_LABELS: Record<string, string> = {
  fat_loss: 'Сваляне на мазнини',
  muscle_gain: 'Мускулна маса',
  maintenance: 'Поддържане',
};

const ACTIVITY_LEVEL_LABELS: Record<string, string> = {
  sedentary: 'Заседнал',
  light: 'Лека активност',
  moderate: 'Умерена активност',
  active: 'Активен',
  very_active: 'Много активен',
};

export default function NutritionistSettingsTab() {
  const [profile, setProfile] = useState<NutritionistProfileData | null>(null);
  const [draft, setDraft] = useState<NutritionistProfileData>({
    isActive: true,
    dailyCalorieTarget: null,
    goalType: 'maintenance',
    activityLevel: 'moderate',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await NutritionService.getNutritionistProfile();
        if (cancelled) return;
        setProfile(data);
        setDraft(data);
      } catch {
        if (!cancelled) setError('Неуспешно зареждане на профила.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const result = await NutritionService.updateNutritionistProfile({
        isActive: draft.isActive,
        dailyCalorieTarget: draft.dailyCalorieTarget,
        goalType: draft.goalType,
        activityLevel: draft.activityLevel,
      });
      setProfile(result);
      setDraft(result);
      setSuccess('Настройките са запазени.');
    } catch {
      setError('Неуспешно запазване.');
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        Нутриционистки агент
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={draft.isActive}
            onChange={(e) => {
              setDraft((prev) => ({ ...prev, isActive: e.target.checked }));
              setSuccess(null);
            }}
          />
        }
        label="Активиране на нутриционистки агент (Coach Nora)"
        sx={{ mb: 2, display: 'block' }}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Дневен калориен таргет"
            type="number"
            value={draft.dailyCalorieTarget ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setDraft((prev) => ({ ...prev, dailyCalorieTarget: val ? Number(val) : null }));
              setSuccess(null);
            }}
            fullWidth
            inputProps={{ min: 0, max: 10000 }}
            placeholder="напр. 2200"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Цел</InputLabel>
            <Select
              label="Цел"
              value={draft.goalType}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, goalType: e.target.value }));
                setSuccess(null);
              }}
            >
              {Object.entries(GOAL_TYPE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Ниво на активност</InputLabel>
            <Select
              label="Ниво на активност"
              value={draft.activityLevel}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, activityLevel: e.target.value }));
                setSuccess(null);
              }}
            >
              {Object.entries(ACTIVITY_LEVEL_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Запази
        </Button>
      </Box>
    </Box>
  );
}
